import React, { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, FileText, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface CoverLetterUploadProps {
  onTemplateUploaded?: (templateUrl: string) => void;
}

export function CoverLetterUpload({ onTemplateUploaded }: CoverLetterUploadProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadedTemplate, setUploadedTemplate] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file: File) => {
    // Validate file type - support .docx and .doc files
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/msword' // .doc
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload a Microsoft Word document (.docx or .doc)",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload a file smaller than 10MB",
        variant: "destructive"
      });
      return;
    }

    setUploadedTemplate(file);
    toast({
      title: "Template selected",
      description: `Selected: ${file.name}`,
    });
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const uploadTemplate = async () => {
    if (!uploadedTemplate || !user) {
      toast({
        title: "Upload failed",
        description: "Please select a template file and ensure you're logged in",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);

    try {
      // Create unique filename with user ID
      const fileName = `${user.id}/cover-letter-template-${Date.now()}.${uploadedTemplate.name.split('.').pop()}`;
      
      const { data, error } = await supabase.storage
        .from('cover-letter-templates')
        .upload(fileName, uploadedTemplate);

      if (error) throw error;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('cover-letter-templates')
        .getPublicUrl(fileName);

      toast({
        title: "Template uploaded",
        description: "Your cover letter template has been uploaded successfully",
      });

      onTemplateUploaded?.(urlData.publicUrl);
      
    } catch (error) {
      console.error('Error uploading template:', error);
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Failed to upload template",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const removeTemplate = () => {
    setUploadedTemplate(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>Upload Cover Letter Template</CardTitle>
        <CardDescription>
          Upload your Microsoft Word cover letter template. This template will be used to generate properly formatted cover letters.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {!uploadedTemplate ? (
          <>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragOver
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/25 hover:border-primary/50'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-semibold text-lg mb-2">Drop your template here or click to upload</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Supports .docx and .doc files up to 10MB
              </p>
              <Button 
                variant="outline" 
                onClick={() => fileInputRef.current?.click()}
              >
                Choose File
              </Button>
            </div>

            <Input
              ref={fileInputRef}
              type="file"
              accept=".docx,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
              onChange={handleFileInputChange}
              className="hidden"
            />
          </>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
              <FileText className="w-8 h-8 text-primary" />
              <div className="flex-1">
                <p className="font-medium">{uploadedTemplate.name}</p>
                <p className="text-sm text-muted-foreground">
                  {(uploadedTemplate.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={removeTemplate}
                className="text-muted-foreground hover:text-destructive"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <Button 
              onClick={uploadTemplate} 
              disabled={uploading}
              className="w-full"
            >
              {uploading ? "Uploading..." : "Upload Template"}
            </Button>
          </div>
        )}

        <div className="bg-muted/50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Template Requirements:</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Must be a Microsoft Word document (.docx or .doc)</li>
            <li>• Keep your writing professional and personalized</li>
            <li>• Use "Body of the letter goes here" where letter content should appear</li>
            <li>• Include header fields: Full Name, Phone, Email, City ST, LinkedIn</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}